/**
 * Dashboard Templates
 *
 * Pre-configured dashboard layouts for different audiences and use cases.
 * Each template defines a set of widgets with their positions and sizes,
 * optimized for the target screen and user type.
 *
 * Bruker kun de 14 referanse-widgetene. Flere templates kan bygges ut
 * etter hvert som nye widgets kommer til.
 *
 * @author BizClick WMS
 * @version 3.0.0
 */

import { WidgetInstance } from '../types';

/**
 * Template definition with metadata
 */
export interface DashboardTemplate {
  id: string;
  name: string;
  description: string;
  audience: string;
  screen: string;
  icon: string;
  color: string;
  widgets: WidgetInstance[];
  gridColumns: number;
  gridRows: number;
}

// Helper to create a widget instance
let instanceCounter = 0;
function w(
  widgetId: string,
  x: number,
  y: number,
  width: number,
  height: number,
  refreshInterval: number = 30
): WidgetInstance {
  instanceCounter++;
  return {
    instanceId: `tmpl-${instanceCounter}`,
    widgetId,
    position: { x, y },
    size: { width, height },
    settings: { refreshInterval, showHeader: true },
  };
}

function resetCounter(): void {
  instanceCounter = 0;
}

// ================================================================
// TEMPLATE 1: Lagergulvet TV
// ================================================================
function createWarehouseFloorTV(): DashboardTemplate {
  resetCounter();
  return {
    id: 'warehouse-floor-tv',
    name: 'Lagergulvet TV',
    description: 'Optimert for stor TV-skjerm p\u00e5 lagergulvet. Viser sanntidsaktivitet, plukkefremdrift og toppliste.',
    audience: 'Operat\u00f8rer',
    screen: 'TV 55\u201d+',
    icon: 'Tv',
    color: 'blue',
    gridColumns: 12,
    gridRows: 8,
    widgets: [
      w('animated-counter', 0, 0, 3, 2, 30),
      w('shipping-stats', 3, 0, 3, 2, 30),
      w('sparkline-kpi', 6, 0, 3, 2, 60),
      w('mini-heatmap', 9, 0, 3, 2, 30),
      w('leaderboard', 0, 2, 4, 4, 60),
      w('remaining-orders', 4, 2, 5, 2, 30),
      w('bottleneck-detector', 9, 2, 3, 2, 60),
      w('activity-feed', 4, 4, 5, 4, 10),
      w('comparison', 9, 4, 3, 2, 60),
      w('operator-status', 0, 6, 4, 2, 30),
      w('order-flow', 9, 6, 3, 2, 60),
    ],
  };
}

// ================================================================
// TEMPLATE 2: Lagerleder Oversikt
// ================================================================
function createManagerOverview(): DashboardTemplate {
  resetCounter();
  return {
    id: 'manager-overview',
    name: 'Lagerleder Oversikt',
    description: 'Komplett oversikt for lagersjef med ordrer, plukking, shipping, operat\u00f8rer og flaskehalser.',
    audience: 'Lagersjef',
    screen: 'Desktop 27\u201d+',
    icon: 'LayoutDashboard',
    color: 'emerald',
    gridColumns: 12,
    gridRows: 10,
    widgets: [
      w('animated-counter', 0, 0, 3, 2, 30),
      w('sparkline-kpi', 3, 0, 3, 2, 60),
      w('shipping-stats', 6, 0, 3, 2, 30),
      w('mini-heatmap', 9, 0, 3, 2, 30),
      w('order-flow', 0, 2, 4, 4, 60),
      w('remaining-orders', 4, 2, 5, 2, 30),
      w('bottleneck-detector', 9, 2, 3, 2, 60),
      w('operator-status', 4, 4, 5, 4, 30),
      w('zone-utilization', 9, 4, 3, 4, 300),
      w('activity-feed', 0, 6, 4, 4, 10),
      w('comparison', 4, 8, 5, 2, 60),
    ],
  };
}

// ================================================================
// TEMPLATE 3: Mottakssone
// ================================================================
function createReceivingZone(): DashboardTemplate {
  resetCounter();
  return {
    id: 'receiving-zone',
    name: 'Mottakssone',
    description: 'Fokus p\u00e5 mottak, innkommende leveranser og plasseringsstatus.',
    audience: 'Mottaksoperat\u00f8rer',
    screen: 'TV/Tablet',
    icon: 'Truck',
    color: 'violet',
    gridColumns: 12,
    gridRows: 8,
    widgets: [
      w('incoming-deliveries', 0, 0, 7, 4, 120),
      w('animated-counter', 7, 0, 3, 2, 30),
      w('sparkline-kpi', 10, 0, 2, 2, 60),
      w('zone-utilization', 7, 2, 5, 4, 300),
      w('activity-feed', 0, 4, 4, 4, 10),
      w('operator-status', 4, 4, 3, 4, 30),
      w('comparison', 7, 6, 5, 2, 60),
    ],
  };
}

// ================================================================
// TEMPLATE 4: Pakke & Send
// ================================================================
function createPackAndShip(): DashboardTemplate {
  resetCounter();
  return {
    id: 'pack-and-ship',
    name: 'Pakke & Send',
    description: 'Optimert for pakkestasjoner med fokus p\u00e5 ordreflyt, forsendelser og gjenst\u00e5ende oppdrag.',
    audience: 'Pakkestasjoner',
    screen: 'TV/Desktop',
    icon: 'Package',
    color: 'cyan',
    gridColumns: 12,
    gridRows: 8,
    widgets: [
      w('shipping-stats', 0, 0, 4, 2, 30),
      w('animated-counter', 4, 0, 4, 2, 30),
      w('sparkline-kpi', 8, 0, 4, 2, 60),
      w('order-flow', 0, 2, 4, 4, 60),
      w('remaining-orders', 4, 2, 5, 2, 30),
      w('bottleneck-detector', 9, 2, 3, 2, 60),
      w('activity-feed', 4, 4, 5, 4, 10),
      w('comparison', 9, 4, 3, 2, 60),
      w('operator-status', 0, 6, 4, 2, 30),
      w('mini-heatmap', 9, 6, 3, 2, 30),
    ],
  };
}

// ================================================================
// TEMPLATE 5: Vestibyle/Wow
// ================================================================
function createVestibuleWow(): DashboardTemplate {
  resetCounter();
  return {
    id: 'vestibule-wow',
    name: 'Vestibylen',
    description: 'Visuelt imponerende for bes\u00f8kende med store animerte tall, heatmap og leaderboard.',
    audience: 'Bes\u00f8kende',
    screen: 'TV 65\u201d+',
    icon: 'Sparkles',
    color: 'rose',
    gridColumns: 12,
    gridRows: 8,
    widgets: [
      w('animated-counter', 0, 0, 4, 3, 30),
      w('animated-counter', 4, 0, 4, 3, 30),
      w('animated-counter', 8, 0, 4, 3, 30),
      w('mini-heatmap', 0, 3, 5, 3, 30),
      w('leaderboard', 5, 3, 4, 5, 60),
      w('sparkline-kpi', 9, 3, 3, 3, 60),
      w('comparison', 0, 6, 5, 2, 60),
      w('activity-feed', 9, 6, 3, 2, 10),
    ],
  };
}

// ================================================================
// TEMPLATE 6: Compass AI Fokus
// ================================================================
function createCompassFocus(): DashboardTemplate {
  resetCounter();
  return {
    id: 'compass-focus',
    name: 'Compass AI Fokus',
    description: 'Dashboard bygget rundt Compass-rapporter — plassholdere der brukeren selv fyller inn rapporter.',
    audience: 'Ledelse / analytikere',
    screen: 'Desktop',
    icon: 'Compass',
    color: 'blue',
    gridColumns: 12,
    gridRows: 8,
    widgets: [
      w('compass-report', 0, 0, 6, 4, 300),
      w('compass-report', 6, 0, 6, 4, 300),
      w('comparison', 0, 4, 6, 2, 60),
      w('sparkline-kpi', 6, 4, 3, 2, 60),
      w('animated-counter', 9, 4, 3, 2, 30),
      w('order-flow', 0, 6, 4, 2, 60),
      w('activity-feed', 4, 6, 4, 2, 10),
      w('bottleneck-detector', 8, 6, 4, 2, 60),
    ],
  };
}

// ================================================================
// Registry
// ================================================================

/**
 * All available dashboard templates
 */
export const DASHBOARD_TEMPLATES: DashboardTemplate[] = [
  createWarehouseFloorTV(),
  createManagerOverview(),
  createReceivingZone(),
  createPackAndShip(),
  createVestibuleWow(),
  createCompassFocus(),
];

/**
 * Get a template by its ID
 */
export function getTemplateById(id: string): DashboardTemplate | undefined {
  return DASHBOARD_TEMPLATES.find((t) => t.id === id);
}

/**
 * Create a fresh layout from a template (generates new instance IDs)
 */
export function createLayoutFromTemplate(
  templateId: string,
  layoutName?: string
): {
  id: string;
  name: string;
  widgets: WidgetInstance[];
  gridColumns: number;
  gridRows: number;
  createdAt: string;
  updatedAt: string;
  isDefault: boolean;
} | null {
  const template = getTemplateById(templateId);
  if (!template) return null;

  const now = new Date().toISOString();
  const layoutId = `layout-${Date.now()}`;

  // Generate fresh instance IDs
  const widgets = template.widgets.map((w, i) => ({
    ...w,
    instanceId: `${layoutId}-w${i + 1}`,
  }));

  return {
    id: layoutId,
    name: layoutName ?? template.name,
    widgets,
    gridColumns: template.gridColumns,
    gridRows: template.gridRows,
    createdAt: now,
    updatedAt: now,
    isDefault: false,
  };
}
